import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { getApiUrl } from '../utils/api';
import { useVault } from '../contexts/VaultContext';

import DropZone from '../components/DropZone';
import RegulationSelector from '../components/RegulationSelector';
import SessionsList from '../components/SessionsList';

interface Session {
  id: string;
  created_at: string;
  regulation: string;
  file_name?: string;
  summary?: string;
  messages: Array<{
    role: string;
    content: string;
  }>;
}

const HomePage: React.FC = () => {
  const [selectedJurisdiction, setSelectedJurisdiction] = useState<string>('');
  const [selectedRegulations, setSelectedRegulations] = useState<string[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [webSearchEnabled, setWebSearchEnabled] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [uploadedImages, setUploadedImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [inputText, setInputText] = useState('');
  const [regulations, setRegulations] = useState<Array<{id: string, name: string}>>([]);
  const [loadingRegulations, setLoadingRegulations] = useState(true);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [projectFiles, setProjectFiles] = useState<File[]>([]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showProjectSelector, setShowProjectSelector] = useState(false);
  const projectSelectorRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { projects, getProject } = useVault();

  useEffect(() => {
    // Load sessions on component mount
    fetchSessions();
  }, []);

  useEffect(() => {
    // Handle click outside of project selector
    const handleClickOutside = (event: MouseEvent) => {
      if (projectSelectorRef.current && !projectSelectorRef.current.contains(event.target as Node)) {
        setShowProjectSelector(false);
      }
    };

    if (showProjectSelector) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProjectSelector]);

  useEffect(() => {
    // Load regulations when jurisdiction is selected
    if (selectedJurisdiction) {
      fetchRegulations();
    } else {
      setRegulations([]);
      setLoadingRegulations(true);
    }
    // Clear selected regulations when jurisdiction changes
    setSelectedRegulations([]);
  }, [selectedJurisdiction]);

  const fetchSessions = async () => {
    try {
      const response = await api.get('sessions');
      // Sort sessions by created_at date, newest first
      const sortedSessions = [...response.data].sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setSessions(sortedSessions);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  };

  const fetchRegulations = async () => {
    try {
      if (selectedJurisdiction === 'UK' || selectedJurisdiction === 'Philippines') {
        const response = await api.get('regulations');
        // Filter regulations by selected jurisdiction
        const filteredRegulations = response.data.filter(
          (reg: any) => reg.jurisdiction === selectedJurisdiction
        );
        setRegulations(filteredRegulations);
      } else if (selectedJurisdiction === 'EU') {
        // EU regulations - empty for now
        setRegulations([]);
      }
    } catch (error) {
      console.error('Error fetching regulations:', error);
      // Fallback to default regulations for UK
      if (selectedJurisdiction === 'UK') {
        setRegulations([
          { id: "uk_bribery_act", name: "UK Bribery Act" },
          { id: "gdpr", name: "GDPR" },
          { id: "data_protection_act_2018", name: "Data Protection Act 2018" }
        ]);
      } else {
        setRegulations([]);
      }
    } finally {
      setLoadingRegulations(false);
    }
  };

  const handleFileUpload = async (file: File | null, text: string | null) => {
    if (!selectedJurisdiction) {
      alert('Please select a jurisdiction first');
      return;
    }
    if (selectedRegulations.length === 0 && selectedJurisdiction !== 'Philippines') {
      alert('Please select at least one regulation');
      return;
    }

    setIsAnalyzing(true);

    try {
      const formData = new FormData();
      // Join multiple regulations with comma, or use default for Philippines
      const regulationValue = selectedJurisdiction === 'Philippines' 
        ? 'philippines_comprehensive' 
        : selectedRegulations.join(',');
      formData.append('regulation', regulationValue);

      if (file) {
        formData.append('file', file);
      } else if (text) {
        formData.append('text_content', text);
      }

      const response = await api.post('analyze', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Navigate to the session page after successful analysis
      navigate(`/session/${response.data.session_id}`);
    } catch (error) {
      console.error('Error analyzing document:', error);
      alert('An error occurred while analyzing the document');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleDeleteSession = async (sessionId: string) => {
    try {
      await api.delete(`sessions/${sessionId}`);
      // Refresh sessions after deletion
      fetchSessions();
    } catch (error) {
      console.error('Error deleting session:', error);
      alert('An error occurred while deleting the session');
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setUploadedFiles(files);
  };

  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const compressImage = (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        const tryCompress = (maxDimension: number, quality: number): Promise<File> => {
          return new Promise((resolveCompress) => {
            // Calculate new dimensions
            let { width, height } = img;
            
            if (width > maxDimension || height > maxDimension) {
              const ratio = Math.min(maxDimension / width, maxDimension / height);
              width *= ratio;
              height *= ratio;
            }
            
            canvas.width = width;
            canvas.height = height;
            
            // Clear and draw
            ctx!.clearRect(0, 0, canvas.width, canvas.height);
            ctx!.drawImage(img, 0, 0, width, height);
            
            canvas.toBlob(
              (blob) => {
                const compressedFile = new File([blob!], file.name.replace(/\.[^/.]+$/, '.jpg'), {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                resolveCompress(compressedFile);
              },
              'image/jpeg',
              quality
            );
          });
        };

        // Progressive compression - keep trying smaller sizes and lower quality until under 1MB
        const compressProgressive = async () => {
          const targetSize = 1024 * 1024; // 1MB target
          const attempts = [
            { dimension: 1024, quality: 0.8 },
            { dimension: 800, quality: 0.7 },
            { dimension: 600, quality: 0.6 },
            { dimension: 512, quality: 0.5 },
            { dimension: 400, quality: 0.4 },
            { dimension: 300, quality: 0.3 },
            { dimension: 200, quality: 0.2 }
          ];

          for (const attempt of attempts) {
            const compressed = await tryCompress(attempt.dimension, attempt.quality);
            console.log(`Compression attempt: ${attempt.dimension}px @ ${attempt.quality} quality = ${compressed.size} bytes`);
            
            if (compressed.size <= targetSize) {
              console.log(`✓ Compression successful: ${file.size} → ${compressed.size} bytes`);
              resolve(compressed);
              return;
            }
          }
          
          // If all attempts failed, use the last (most compressed) version
          const finalCompressed = await tryCompress(200, 0.1);
          console.log(`⚠ Using maximum compression: ${file.size} → ${finalCompressed.size} bytes`);
          resolve(finalCompressed);
        };

        compressProgressive().catch(reject);
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  };

  const handleImageSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length === 0) return;
    
    try {
      console.log(`📸 Processing ${imageFiles.length} images...`);
      
      const compressedImages: File[] = [];
      const newPreviews: string[] = [];
      
      for (const file of imageFiles) {
        console.log(`📸 Original image: ${file.name}, ${(file.size / 1024 / 1024).toFixed(2)} MB`);
        
        // Always compress the image regardless of original size
        const compressedFile = await compressImage(file);
        console.log(`✅ Final compressed image: ${(compressedFile.size / 1024).toFixed(1)} KB`);
        
        compressedImages.push(compressedFile);
        
        // Create preview URL from compressed image
        const reader = new FileReader();
        const preview = await new Promise<string>((resolve) => {
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(compressedFile);
        });
        newPreviews.push(preview);
      }
      
      setUploadedImages(compressedImages);
      setImagePreviews(newPreviews);
      
    } catch (error) {
      console.error('❌ Error compressing images:', error);
      alert('Error processing images. Please try again.');
      event.target.value = '';
    }
  };

  const removeImage = (index: number) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const downloadProjectFile = async (projectId: string, fileId: string, fileName: string): Promise<File | null> => {
    try {
      const token = localStorage.getItem('token');
      console.log(`Attempting to download file: ${fileName} (ID: ${fileId}) from project ${projectId}`);
      
      const response = await fetch(`http://localhost:8000/api/projects/${projectId}/files/${fileId}/download`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      console.log(`Download response for ${fileName}: ${response.status} ${response.statusText}`);
      
      if (!response.ok) {
        console.error(`Failed to download file ${fileName}: ${response.status} ${response.statusText}`);
        return null;
      }
      
      const blob = await response.blob();
      console.log(`Successfully downloaded ${fileName}, blob size: ${blob.size} bytes`);
      return new File([blob], fileName, { type: blob.type });
    } catch (error) {
      console.error(`Error downloading file ${fileName}:`, error);
      return null;
    }
  };

  const handleProjectSelection = async (projectId: string) => {
    if (!projectId) {
      setSelectedProject('');
      setProjectFiles([]);
      setUploadedFiles([]);
      return;
    }

    const project = getProject(projectId);
    if (!project) {
      console.error('Project not found:', projectId);
      return;
    }

    setSelectedProject(projectId);
    
    // Clear any existing uploaded files when selecting a new project
    setUploadedFiles([]);
    setUploadedImages([]);
    setImagePreviews([]);
    
    // Automatically include project files that have stored File objects
    if (project.files.length > 0) {
      console.log(`Loading ${project.files.length} files from project "${project.name}"...`);
      console.log('Project files:', project.files);
      
      const availableFiles: File[] = [];
      const availableImages: File[] = [];
      const unavailableFiles: string[] = [];
      
      for (const projectFile of project.files) {
        console.log(`Processing file: ${projectFile.file_name} (${projectFile.id})`);
        
        if (projectFile.fileObject) {
          // Check if it's an image and needs compression
          if (projectFile.fileObject.type.startsWith('image/')) {
            console.log(`📸 Found image: ${projectFile.file_name}, will compress before adding`);
            try {
              const compressedFile = await compressImage(projectFile.fileObject);
              availableImages.push(compressedFile);
              console.log(`✅ Compressed image: ${projectFile.file_name} -> ${(compressedFile.size / 1024).toFixed(1)} KB`);
            } catch (error) {
              console.error(`❌ Failed to compress image ${projectFile.file_name}:`, error);
              // Fall back to original if compression fails
              availableFiles.push(projectFile.fileObject);
            }
          } else {
            // Non-image file, use directly
            availableFiles.push(projectFile.fileObject);
            console.log(`✅ Using cached file object: ${projectFile.file_name}`);
          }
        } else {
          // File was uploaded to server, try to download it
          unavailableFiles.push(projectFile.file_name);
          console.log(`⚠️  File ${projectFile.file_name} was uploaded to server - download not implemented yet`);
        }
      }
      
      console.log(`File loading summary: ${availableFiles.length} files + ${availableImages.length} compressed images available locally, ${unavailableFiles.length} on server`);
      
      if (availableFiles.length > 0) {
        setUploadedFiles(availableFiles);
        console.log(`Set uploadedFiles count: ${availableFiles.length}`);
      }
      
      if (availableImages.length > 0) {
        setUploadedImages(availableImages);
        console.log(`Set uploadedImages count: ${availableImages.length}`);
        
        // Create previews for the compressed images
        const newPreviews: string[] = [];
        for (const image of availableImages) {
          const reader = new FileReader();
          const preview = await new Promise<string>((resolve) => {
            reader.onloadend = () => resolve(reader.result as string);
            reader.readAsDataURL(image);
          });
          newPreviews.push(preview);
        }
        setImagePreviews(newPreviews);
      }
      
      console.log(`✅ Successfully loaded ${availableFiles.length + availableImages.length} files from vault project "${project.name}"`);
      
      if (unavailableFiles.length > 0) {
        console.warn(`⚠️  ${unavailableFiles.length} files are stored on server and need to be manually re-uploaded: ${unavailableFiles.join(', ')}`);
      }
    } else {
      console.log('Project has no files to load');
    }
    
    setProjectFiles([]);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const handleSendMessage = async () => {
    console.log('=== FRONTEND SEND MESSAGE CALLED ===');
    console.log('inputText:', inputText);
    console.log('uploadedFiles:', uploadedFiles);
    console.log('uploadedImages:', uploadedImages);
    console.log('selectedProject:', selectedProject);
    console.log('selectedJurisdiction:', selectedJurisdiction);
    console.log('selectedRegulations:', selectedRegulations);
    
    if (!inputText.trim() && uploadedFiles.length === 0 && uploadedImages.length === 0) {
      console.log('VALIDATION FAILED: No input text, files, or images');
      return;
    }
    
    // Require jurisdiction selection
    if (!selectedJurisdiction) {
      console.log('VALIDATION FAILED: No jurisdiction selected');
      alert('Please select a jurisdiction first');
      return;
    }
    
    // Require regulation selection if files or images are uploaded (except for Philippines)
    if ((uploadedFiles.length > 0 || uploadedImages.length > 0) && selectedRegulations.length === 0 && selectedJurisdiction !== 'Philippines') {
      console.log('VALIDATION FAILED: Files/images uploaded but no regulations selected');
      alert('Please select at least one regulation to analyze against');
      return;
    }

    console.log('=== VALIDATION PASSED - CREATING REQUEST ===');
    setIsAnalyzing(true);

    try {
      console.log('=== CREATING FORM DATA ===');
      const formData = new FormData();
      
      // Use selected regulations, or special handling for Philippines
      let regulationsToUse;
      if (selectedJurisdiction === 'Philippines') {
        regulationsToUse = 'philippines_comprehensive';
      } else if (selectedRegulations.length > 0) {
        regulationsToUse = selectedRegulations.join(',');
      } else {
        regulationsToUse = 'general';
      }
      console.log('regulationsToUse:', regulationsToUse);
      formData.append('regulation', regulationsToUse);
      
      // Add multiple files
      uploadedFiles.forEach((file, index) => {
        console.log(`Adding file ${index + 1} to form data:`, file.name);
        formData.append('files', file);
      });
      
      if (inputText.trim()) {
        console.log('Adding text content and initial message:', inputText.trim());
        formData.append('text_content', inputText.trim());
        formData.append('initial_message', inputText.trim());
      }
      
      // Add multiple images
      uploadedImages.forEach((image, index) => {
        console.log(`Adding image ${index + 1} to form data:`, image.name, image.size);
        formData.append('images', image);
      });

      // Add project information if a project is selected
      if (selectedProject) {
        const project = getProject(selectedProject);
        if (project) {
          console.log('Adding project metadata:', project.name);
          formData.append('project_name', project.name);
          formData.append('project_id', project.id);
        }
      }

      const url = getApiUrl('analyze');
      console.log('=== SENDING REQUEST ===');
      console.log('URL:', url);
      console.log('FormData entries:');
      // Convert FormData entries to array for iteration
      Array.from(formData.entries()).forEach(([key, value]) => {
        console.log(`  ${key}:`, value);
      });

      const response = await api.post('analyze', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('=== REQUEST SUCCESS ===');
      console.log('Response:', response.data);
      
      // Navigate to the session page after successful analysis
      navigate(`/session/${response.data.session_id}`);
    } catch (error: any) {
      console.error('=== REQUEST ERROR ===');
      console.error('Error analyzing:', error);
      console.error('Error details:', error.response?.data);
      console.error('Error status:', error.response?.status);
      alert('An error occurred while processing your request');
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="flex h-screen bg-white">
      {/* Sidebar */}
      {!sidebarCollapsed && (
        <SessionsList
          sessions={sessions}
          onDeleteSession={handleDeleteSession}
          showHeader={false}
          onCollapse={() => setSidebarCollapsed(true)}
        />
      )}

      {/* Main content area */}
      <div className="flex-grow flex flex-col px-8 py-6 order-2 relative">
        {/* Sidebar toggle button - only show when sidebar is collapsed */}
        {sidebarCollapsed && (
          <button
            onClick={() => setSidebarCollapsed(false)}
            className="absolute left-0 top-6 z-10 bg-white border border-gray-200 rounded-r-lg px-2 py-3 hover:bg-gray-50 transition-colors duration-200 shadow-sm"
            title="Show sidebar"
          >
            <svg 
              className="w-4 h-4 text-gray-600" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              {/* >> symbol (chevrons right) */}
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 5l7 7-7 7" />
            </svg>
          </button>
        )}
        {/* Jurisdiction and Regulation Selectors in top left */}
        <div className={`mb-6 ${sidebarCollapsed ? 'pl-10' : ''}`}>
          <div className="max-w-sm space-y-4">
            {/* Jurisdiction Selector */}
            <div>
              <label htmlFor="jurisdiction" className="block text-sm font-medium text-gray-700 mb-2">
                Select Jurisdiction
              </label>
              <select
                id="jurisdiction"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-sm"
                value={selectedJurisdiction}
                onChange={(e) => setSelectedJurisdiction(e.target.value)}
              >
                <option value="">Choose jurisdiction...</option>
                <option value="UK">🇬🇧 United Kingdom</option>
                <option value="EU">🇪🇺 European Union</option>
                <option value="Philippines">🇵🇭 Philippines</option>
              </select>
            </div>


            {/* Regulation Selector - only show when jurisdiction is selected AND not Philippines */}
            {selectedJurisdiction && selectedJurisdiction !== 'Philippines' && (
              <div>
                <label htmlFor="regulation" className="block text-sm font-medium text-gray-700 mb-2">
                  Select Regulations
                </label>
                <div className="relative">
                  {/* Selected regulations display */}
                  <div className="flex flex-wrap gap-2 mb-2 min-h-6">
                    {selectedRegulations.length > 0 ? (
                      selectedRegulations.map(regId => {
                        const regulation = regulations.find(r => r.id === regId);
                        return (
                          <div
                            key={regId}
                            className="px-2 py-1 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-full flex items-center text-xs"
                          >
                            <span className="mr-1 text-gray-700 font-medium">{regulation?.name}</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedRegulations(selectedRegulations.filter(id => id !== regId));
                              }}
                              className="text-gray-400 hover:text-red-500 transition-colors duration-150"
                            >
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                            </button>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-gray-500 text-xs italic">No regulations selected</div>
                    )}
                  </div>
                  
                  {selectedJurisdiction === 'EU' ? (
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm text-gray-500">
                      EU regulations coming soon...
                    </div>
                  ) : (
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-sm"
                      onChange={(e) => {
                        if (e.target.value && !selectedRegulations.includes(e.target.value)) {
                          setSelectedRegulations([...selectedRegulations, e.target.value]);
                        }
                        e.target.value = '';
                      }}
                      value=""
                    >
                      <option value="">{loadingRegulations ? 'Loading regulations...' : 'Add regulation...'}</option>
                      {!loadingRegulations && regulations.map(reg => (
                        <option key={reg.id} value={reg.id}>{reg.name}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex-grow flex flex-col justify-start pt-4">
          <div className="w-full max-w-2xl mx-auto">
            {/* Lexa AI Agent Logo and Title */}
            <div className="text-center mb-8">
              <img 
                src="/lexa_chatbot_logo.png" 
                alt="Lexa Logo" 
                className="w-16 h-16 mx-auto mb-3 shadow-lg"
              />
              <h1 className="text-2xl font-bold text-gray-800">Lexa</h1>
            </div>

            {/* Chat Input Interface */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
              <div className="flex flex-col">
                {/* Multiple Files Upload Indicator */}
                {uploadedFiles.length > 0 && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">
                        {uploadedFiles.length} file{uploadedFiles.length > 1 ? 's' : ''} selected
                      </span>
                    </div>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {uploadedFiles.map((file, index) => (
                        <div key={`uploaded-${index}`} className="flex items-center justify-between p-2 bg-white rounded border">
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                              <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                              </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {file.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {formatFileSize(file.size)}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => removeFile(index)}
                            className="flex-shrink-0 p-1 hover:bg-gray-200 rounded-full transition-colors duration-200"
                          >
                            <svg className="w-4 h-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Multiple Images Preview */}
                {uploadedImages.length > 0 && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">
                        {uploadedImages.length} image{uploadedImages.length > 1 ? 's' : ''} selected
                      </span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-48 overflow-y-auto">
                      {uploadedImages.map((image, index) => (
                        <div key={index} className="relative group">
                          <img 
                            src={imagePreviews[index]} 
                            alt={`Image ${index + 1}`} 
                            className="w-full h-20 object-cover rounded border border-gray-300"
                          />
                          <button
                            onClick={() => removeImage(index)}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                            </svg>
                          </button>
                          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b">
                            <p className="truncate">{image.name}</p>
                            <p>{formatFileSize(image.size)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Text Input */}
                <textarea
                  placeholder="Ask anything"
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 outline-none resize-none text-lg"
                  rows={3}
                  disabled={isAnalyzing}
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                />
                
                {/* Action Buttons Row */}
                <div className="flex items-center justify-between mt-4">
                  <div className="flex items-center space-x-4">
                    {/* Project Attachment Button */}
                    <div className="relative" ref={projectSelectorRef}>
                      <button
                        onClick={() => setShowProjectSelector(!showProjectSelector)}
                        className={`flex items-center justify-center w-10 h-10 ${selectedProject ? 'bg-blue-500 hover:bg-blue-600' : 'bg-gray-100 hover:bg-gray-200'} rounded-full transition-colors duration-200`}
                        title={selectedProject ? `Project: ${getProject(selectedProject)?.name}` : 'Attach project from vault'}
                      >
                        <svg className={`w-5 h-5 ${selectedProject ? 'text-white' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                        </svg>
                      </button>
                      
                      {/* Project selector dropdown */}
                      {showProjectSelector && (
                        <div className="absolute bottom-12 left-0 w-64 bg-white rounded-lg shadow-lg border border-gray-200 p-2 z-10">
                          <div className="font-medium text-sm text-gray-700 px-2 py-1 mb-1">Select a project from vault</div>
                          <div className="max-h-48 overflow-y-auto">
                            <button
                              onClick={() => {
                                handleProjectSelection('');
                                setShowProjectSelector(false);
                              }}
                              className="w-full text-left px-2 py-2 hover:bg-gray-100 rounded text-sm text-gray-600"
                            >
                              No project
                            </button>
                            {projects.map(project => (
                              <button
                                key={project.id}
                                onClick={() => {
                                  handleProjectSelection(project.id);
                                  setShowProjectSelector(false);
                                }}
                                className={`w-full text-left px-2 py-2 hover:bg-gray-100 rounded text-sm ${selectedProject === project.id ? 'bg-blue-50 text-blue-700' : ''}`}
                              >
                                <div className="font-medium">{project.name}</div>
                                <div className="text-xs text-gray-500">{project.fileCount} files</div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Selected project indicator */}
                      {selectedProject && (
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full"></div>
                      )}
                    </div>

                    {/* Attach Files Button */}
                    <div className="relative group">
                      <label className="flex items-center justify-center w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors duration-200 cursor-pointer">
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"></path>
                        </svg>
                        <input
                          type="file"
                          className="hidden"
                          onChange={handleFileSelect}
                          accept=".pdf,.docx,.txt"
                          multiple
                        />
                      </label>
                      
                      {/* Tooltip */}
                      <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                        Attach files
                      </div>
                    </div>

                    {/* Attach Image Button */}
                    <div className="relative group">
                      <label className="flex items-center justify-center w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors duration-200 cursor-pointer">
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <input
                          type="file"
                          className="hidden"
                          onChange={handleImageSelect}
                          accept="image/*"
                          multiple
                        />
                      </label>
                      
                      {/* Tooltip */}
                      <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                        Attach image
                      </div>
                    </div>


                    {/* Web Search Toggle */}
                    <div className="flex items-center space-x-2">
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"></path>
                      </svg>
                      <span className="text-sm text-gray-600">Web search</span>
                      <button 
                        onClick={() => setWebSearchEnabled(!webSearchEnabled)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          webSearchEnabled ? 'bg-blue-500' : 'bg-gray-200'
                        }`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-transform ${
                          webSearchEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`}></span>
                      </button>
                    </div>
                  </div>

                  {/* Send Button */}
                  <button 
                    onClick={handleSendMessage}
                    className="flex items-center justify-center w-10 h-10 bg-black hover:bg-gray-800 rounded-full transition-colors duration-200 disabled:opacity-50"
                    disabled={isAnalyzing || (!inputText.trim() && uploadedFiles.length === 0 && uploadedImages.length === 0)}
                  >
                    {isAnalyzing ? (
                      <div className="animate-spin h-5 w-5">
                        <svg className="text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      </div>
                    ) : (
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>
            
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;